// Anita Week 8 / Day 4 — Nature Connections.
// Verbatim from FINAL Week 8 Part B source-of-truth package. No invention.
// SAFETY: drawing / window observation only; parentGuide forbids touching
// unknown plants/animals or going outside alone.

const day4 = {
  id: 'day-4',
  studentId: 'anita',
  weekId: 'week-8',
  dayId: 'day-4',
  status: 'active',
  title: 'Связи в природе: солнце, вода, растения и животные / Nature Connections: Sun, Water, Plants and Animals',
  shortTitle: 'Связи в природе / Nature Connections',
  theme:
    'Анита учится видеть связи в природе: солнце помогает растениям, растения помогают животным, вода нужна живым организмам. / Anita learns to see nature connections: sun helps plants, plants help animals, and water is needed by living organisms.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'nature-connections', 'data', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни смотрит на сад и замечает: солнце светит, вода помогает, растение растёт, насекомое прилетает к цветку, птица ищет пищу. Сегодня Анита узнает, что в природе всё связано. / Nimny looks at the garden and notices: the sun shines, water helps, a plant grows, an insect comes to a flower, and a bird looks for food. Today Anita learns that nature is connected.',
  goals: [
    'Понять, что в природе есть связи. / Understand that nature has connections.',
    'Узнать простую цепочку: солнце → растение → животное. / Learn a simple chain: sun → plant → animal.',
    'Читать таблицу наблюдений. / Read an observation table.',
    'Решать задачи с умножением, делением и сравнением. / Solve multiplication, division and comparison tasks.',
    'Нарисовать связь в природе. / Draw a nature connection.',
    'Объяснить родителю один вывод. / Explain one conclusion to parent.'
  ],
  vocabulary: [
    { term: 'связь / connection', definition: 'когда одно помогает или влияет на другое. / when one thing helps or affects another.' },
    { term: 'солнце / sun', definition: 'даёт свет и тепло. / gives light and warmth.' },
    { term: 'вода / water', definition: 'нужна живым организмам. / needed by living organisms.' },
    { term: 'растение / plant', definition: 'живой организм, который растёт. / a living organism that grows.' },
    { term: 'животное / animal', definition: 'живой организм, который двигается, ест и растёт. / a living organism that moves, eats and grows.' },
    { term: 'пища / food', definition: 'то, что даёт энергию живым организмам. / what gives energy.' },
    { term: 'цепочка / chain', definition: 'порядок связанных шагов. / ordered connected steps.' },
    { term: 'среда / habitat', definition: 'место, где живёт организм. / where an organism lives.' },
    { term: 'наблюдение / observation', definition: 'что я заметил. / what I noticed.' },
    { term: 'вывод / conclusion', definition: 'что я понял после наблюдения. / what I understood after observing.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова связей в природе / Nature Connection Words',
      prompt: 'Прочитай по-русски:\nсвязь\nсолнце\nвода\nрастение\nживотное\nпища\nцепочка\nсреда\nнаблюдение\nвывод',
      ruPrimaryText: 'Прочитай 10 русских слов связей в природе.',
      enHelpText: 'Read the Russian nature connection words first. English is only for help.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nСолнце помогает растению расти.\nВода нужна растениям и животным.\nРастение может быть пищей для животного.\nВ природе всё связано.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про связи в природе.',
      enHelpText: 'The sun helps a plant grow. Water is needed by plants and animals. A plant can be food for an animal. Nature is connected.',
      answer: 'Анита пробует читать 4 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'nature-chain-order',
      type: 'text',
      title: 'Задание 3 — Цепочка природы / Nature Chain',
      prompt:
        'Поставь элементы в правильном порядке.\n\nA. птица\nB. солнце\nC. растение\nD. насекомое\n\nПростая цепочка:\nсолнце → растение → насекомое → птица\n\nОтветь:\n1. Что идёт первым?\n2. Что идёт после растения?\n3. Кто может съесть насекомое?',
      ruPrimaryText: 'Поставь 4 элемента цепочки природы по порядку и ответь на 3 вопроса.',
      enHelpText: 'Order: sun → plant → insect → bird.',
      answer: 'Порядок: B → C → D → A. 1) Солнце. 2) Насекомое. 3) Птица.'
    },
    {
      id: 'nature-needs-sort',
      type: 'classify',
      title: 'Задание 4 — Что кому нужно? / Who Needs What?',
      prompt:
        'Разложи карточки по категориям.\n\nКатегории:\nНужно растениям\nНужно животным\nНужно и растениям, и животным\n\nКарточки:\n1. вода\n2. свет солнца\n3. пища\n4. безопасная среда\n5. воздух\n6. место для роста\n7. место для укрытия\n8. тепло\n9. почва для корней\n10. отдых\n11. пространство\n12. защита от опасности',
      ruPrimaryText: 'Разложи 12 карточек: растениям / животным / обоим.',
      enHelpText: 'Sort 12 cards into needed-by-plants / needed-by-animals / needed-by-both.',
      categories: [
        { id: 'plants', label: 'Нужно растениям / Plants need' },
        { id: 'animals', label: 'Нужно животным / Animals need' },
        { id: 'both', label: 'Нужно обоим / Both need' }
      ],
      items: [
        { id: 'i1', label: 'вода', category: 'both' },
        { id: 'i2', label: 'свет солнца', category: 'plants' },
        { id: 'i3', label: 'пища', category: 'animals' },
        { id: 'i4', label: 'безопасная среда', category: 'both' },
        { id: 'i5', label: 'воздух', category: 'both' },
        { id: 'i6', label: 'место для роста', category: 'plants' },
        { id: 'i7', label: 'место для укрытия', category: 'animals' },
        { id: 'i8', label: 'тепло', category: 'both' },
        { id: 'i9', label: 'почва для корней', category: 'plants' },
        { id: 'i10', label: 'отдых', category: 'animals' },
        { id: 'i11', label: 'пространство', category: 'both' },
        { id: 'i12', label: 'защита от опасности', category: 'both' }
      ],
      answer: 'Растениям: 2, 6, 9. Животным: 3, 7, 10. Обоим: 1, 4, 5, 8, 11, 12. Parent note: some items can be discussed — the key idea is that living organisms have needs, and many overlap.'
    },
    {
      id: 'math-flower-insect',
      type: 'text',
      title: 'Задание 5 — Считаем цветы и насекомых / Count Flowers and Insects',
      prompt:
        'В саду Нимни:\n\n6 групп цветов.\nВ каждой группе по 4 цветка.\nНа каждые 3 цветка прилетает 1 насекомое.\n\nОтветь:\n1. Сколько цветов всего?\n2. Если 1 насекомое на 3 цветка, сколько насекомых прилетит к 24 цветкам?\n3. Если 5 цветов завяли, сколько цветов осталось?\n4. Если осталось 19 цветов, сколько полных групп по 4 цветка можно собрать?',
      ruPrimaryText: 'Реши 4 задачи с умножением и делением про цветы и насекомых.',
      enHelpText: '6 × 4 flowers; 24 ÷ 3 insects; 24 − 5 left; 19 ÷ 4 groups.',
      answer: '1) 6 × 4 = 24 цветка. 2) 24 ÷ 3 = 8 насекомых. 3) 24 − 5 = 19 цветов. 4) 19 ÷ 4 = 4 полные группы и 3 цветка останутся.'
    },
    {
      id: 'math-habitat-table',
      type: 'text',
      title: 'Задание 6 — Таблица среды обитания / Habitat Data Table',
      prompt:
        'Прочитай таблицу наблюдений Нимни:\n\nЛес — 12 животных\nЛуг — 9 животных\nПруд — 6 животных\nСад — 15 животных\n\nОтветь:\n1. Где животных больше всего?\n2. Где животных меньше всего?\n3. На сколько в саду больше животных, чем в пруду?\n4. Сколько животных в лесу и на лугу вместе?\n5. Сколько всего животных в таблице?',
      ruPrimaryText: 'Прочитай таблицу 4 сред обитания и реши 5 задач.',
      enHelpText: 'Forest 12, meadow 9, pond 6, garden 15. Answer 5 questions.',
      answer: '1) В саду. 2) В пруду. 3) 15 − 6 = 9. 4) 12 + 9 = 21. 5) 12 + 9 + 6 + 15 = 42.'
    },
    {
      id: 'data-mini-graph',
      type: 'text',
      title: 'Задание 7 — Мини-график природы / Mini Nature Graph',
      prompt:
        'Сделай мини-график из квадратиков.\n\nДанные:\nрастения — 8\nнасекомые — 6\nптицы — 3\nживотные у пруда — 9\n\nНарисуй:\nрастения: □ □ □ □ □ □ □ □\nнасекомые: □ □ □ □ □ □\nптицы: □ □ □\nживотные у пруда: □ □ □ □ □ □ □ □ □\n\nОтветь:\n1. Какая группа самая большая?\n2. На сколько растений больше, чем птиц?\n3. Сколько всего растений и насекомых?',
      ruPrimaryText: 'Прочитай мини-график природы и ответь на 3 вопроса.',
      enHelpText: 'Mini graph: plants 8, insects 6, birds 3, pond animals 9.',
      answer: '1) Животные у пруда. 2) 8 − 3 = 5. 3) 8 + 6 = 14.'
    },
    {
      id: 'spelling-connections',
      type: 'text',
      title: 'Задание 8 — Правописание / Spelling',
      prompt: 'Напиши:\nсолнце\nвода\nрастение\nпища\nвывод\n\nВставь буквы:\nсо_нце\nв_да\nраст_ние\nп_ща\nвыв_д',
      ruPrimaryText: 'Напиши 5 слов связей и вставь пропущенные буквы.',
      enHelpText: 'Write 5 words and fill in the missing letters.',
      answer: 'солнце, вода, растение, пища, вывод.'
    },
    {
      id: 'creative-nature-chain',
      type: 'text',
      title: 'Задание 9 — Моя цепочка природы / My Nature Chain',
      prompt:
        'Нарисуй цепочку природы.\n\nНа рисунке должны быть:\n1. солнце\n2. вода\n3. растение\n4. животное или насекомое\n5. стрелки связи\n\nНапиши:\nСолнце помогает ______.\nВода нужна ______.\nМой вывод: в природе всё ______.',
      ruPrimaryText: 'Нарисуй цепочку природы и напиши 3 коротких подписи.',
      enHelpText: 'Draw a connection chain and write 3 short labels.',
      hint: 'Используй рисунок — не трогай настоящие растения и животных. / Use a drawing; do not touch real plants or animals.',
      answer: 'Open-ended. Accept if Anita draws a connection chain and writes a simple conclusion.'
    },
    {
      id: 'speech-nature-connection',
      type: 'text',
      title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nСолнце помогает растению.\nВода нужна живым организмам.\nЖивотные зависят от среды.\nМой вывод: в природе всё связано.',
      ruPrimaryText: 'Объясни родителю минимум 2 связи в природе по-русски.',
      enHelpText: 'Tell a parent at least two nature connections in Russian.',
      answer: 'Accept if Anita explains at least two connections in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 4 фразы.',
    '3. Нарисуй простую цепочку: солнце → растение → насекомое → птица.',
    '4. Разложи 8 карточек «что кому нужно» устно с родителем.',
    '5. Реши задачи про цветы и насекомых.',
    '6. Прочитай таблицу среды обитания.',
    '7. Нарисуй мини-график.',
    '8. Нарисуй свою цепочку природы.',
    '9. Объясни родителю вывод.'
  ],
  printWorksheet: {
    title: 'Связи в природе: солнце, вода, растения и животные / Nature Connections',
    subtitle: 'Неделя 8 · День 4 / Week 8 · Day 4',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни смотрит на сад и замечает связи: солнце, вода, растения, насекомые и животные помогают природе работать как система. / Nimny watches the garden and notices nature works as a connected system.' },
          { type: 'text', text: 'Задание 1 — Слова связей в природе. Прочитай и обведи:' },
          { type: 'list', items: [
            'связь / connection',
            'солнце / sun',
            'вода / water',
            'растение / plant',
            'животное / animal',
            'пища / food',
            'цепочка / chain',
            'среда / habitat',
            'наблюдение / observation',
            'вывод / conclusion'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку:' },
          { type: 'list', items: [
            '□ Солнце помогает растению расти.',
            '□ Вода нужна растениям и животным.',
            '□ Растение может быть пищей для животного.',
            '□ В природе всё связано.'
          ] },
          { type: 'text', text: 'English help: Nature is connected. Sun helps plants. Water helps living things.' },
          { type: 'text', text: 'Задание 3 — Цепочка природы. A. птица. B. солнце. C. растение. D. насекомое. Правильный порядок: ____ → ____ → ____ → ____.' },
          { type: 'list', items: [
            '1. Что идёт первым? __________________________',
            '2. Что идёт после растения? __________________________',
            '3. Кто может съесть насекомое? __________________________'
          ] }
        ]
      },
      {
        title: 'Карточки, таблица и математика / Cards, Table and Math',
        blocks: [
          { type: 'text', text: 'Задание 4 — Что кому нужно? Напиши Р = растениям, Ж = животным, О = обоим.' },
          { type: 'list', items: [
            '1. вода ____',
            '2. свет солнца ____',
            '3. пища ____',
            '4. безопасная среда ____',
            '5. воздух ____',
            '6. место для роста ____',
            '7. место для укрытия ____',
            '8. тепло ____',
            '9. почва для корней ____',
            '10. отдых ____',
            '11. пространство ____',
            '12. защита от опасности ____'
          ] },
          { type: 'text', text: 'Задание 5 — Считаем цветы и насекомых (умножение и деление):' },
          { type: 'list', items: [
            '6 групп цветов × 4 цветка = ______ цветов',
            '24 цветка ÷ 3 = ______ насекомых',
            '24 − 5 = ______ цветов осталось',
            '19 ÷ 4 = ______ полные группы и ______ цветка останутся'
          ] },
          { type: 'text', text: 'Задание 6 — Таблица среды обитания. Лес — 12 животных, луг — 9 животных, пруд — 6 животных, сад — 15 животных.' },
          { type: 'list', items: [
            '1. Где животных больше всего? __________________________',
            '2. Где животных меньше всего? __________________________',
            '3. Сад − пруд: 15 − 6 = ______',
            '4. Лес + луг: 12 + 9 = ______',
            '5. Всего: 12 + 9 + 6 + 15 = ______'
          ] }
        ]
      },
      {
        title: 'График, правописание и вывод / Graph, Spelling and Conclusion',
        blocks: [
          { type: 'text', text: 'Задание 7 — Мини-график природы. растения: □ □ □ □ □ □ □ □ ; насекомые: □ □ □ □ □ □ ; птицы: □ □ □ ; животные у пруда: □ □ □ □ □ □ □ □ □.' },
          { type: 'list', items: [
            '1. Самая большая группа: __________________________',
            '2. Растений больше, чем птиц, на: ______',
            '3. Растения + насекомые = ______'
          ] },
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'солнце   вода   растение   пища   вывод',
            'со_нце     в_да     раст_ние     п_ща     выв_д'
          ] },
          { type: 'text', text: 'Задание 9 — Моя цепочка природы. Нарисуй:' },
          { type: 'list', items: [
            '□ солнце',
            '□ вода',
            '□ растение',
            '□ животное или насекомое',
            '□ стрелки связи'
          ] },
          { type: 'text', text: 'Солнце помогает __________________________. Вода нужна __________________________. Мой вывод: в природе всё __________________________.' },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Скажи:' },
          { type: 'list', items: [
            '□ Солнце помогает растению.',
            '□ Вода нужна живым организмам.',
            '□ Животные зависят от среды.',
            '□ Мой вывод: в природе всё связано.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 4 фразы  □ решила задачи с × и ÷  □ прочитала таблицу  □ сделала мини-график  □ нарисовала цепочку природы  □ объяснила вывод родителю.' },
          { type: 'text', text: 'Notebook Mode fallback — если нет принтера, сделай все 10 заданий в тетради по тем же номерам.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает простые связи в природе: солнце, вода, растения, животные и среда связаны между собой.',
    mathGoal: 'Умножение (6 × 4), деление (24 ÷ 3, 19 ÷ 4), сложение, вычитание, таблица данных, мини-график, многошаговые задачи.',
    russianGoal: 'Прочитать слова связь, солнце, вода, растение, животное, пища, цепочка, среда, наблюдение, вывод.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 5 слов, подписать цепочку природы и вывод.',
    practicalGoal: 'Нарисовать простую цепочку природы и объяснить связь.',
    parentTheoryCard: {
      title: 'Как объяснить связи в природе',
      purpose: 'Помочь родителю объяснить, что природа работает как система, где элементы зависят друг от друга.',
      keyMessage: 'В природе многое связано: солнце, вода, растения, животные и среда помогают друг другу существовать.',
      parentScript: [
        '«Растение не растёт само по себе: ему нужны свет, вода, воздух и место.»',
        '«Животное зависит от пищи, воды и безопасной среды.»',
        '«Если один элемент меняется, это может повлиять на другие элементы.»',
        '«Мы изучаем связи, чтобы понимать природу, а не просто запоминать слова.»'
      ],
      practiceSteps: [
        'Прочитайте цепочку солнце → растение → насекомое → птица.',
        'Попросите Аниту объяснить каждую стрелку.',
        'Разберите таблицу среды обитания.',
        'Нарисуйте мини-график.',
        'Попросите сделать один вывод.'
      ],
      doNotSay: [
        'Не говорить, что природа простая и всегда одинаковая.',
        'Не заставлять ребёнка трогать растения или животных.',
        'Не просить искать насекомых или животных без родителя.'
      ],
      paperOnlyNotes: [
        'Можно рисовать цепочки в тетради.',
        'Наблюдения можно делать из окна или по картинкам.',
        'Не собирать живые организмы для урока.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится видеть связи между живыми организмами и условиями среды.',
    keyMessage: 'Если я вижу связь, я лучше понимаю природу.',
    howToExplain: 'Скажите: «Солнце, вода, растения и животные не отдельно друг от друга. Они связаны. Учёный ищет эти связи.»',
    exampleDialogue:
      'Parent: Что помогает растению расти?\nChild: Солнце и вода.\nParent: Кому может помочь растение?\nChild: Насекомому или животному.\nParent: Какой вывод?\nChild: В природе всё связано.',
    readingSupport: 'Слова «наблюдение», «цепочка», «животное» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Солнце помогает растению. Вода нужна живым организмам. В природе всё связано.»',
    mathAnswerKey:
      'nature-chain-order: B → C → D → A; солнце; насекомое; птица.\nmath-flower-insect: 1) 24. 2) 8. 3) 19. 4) 4 полные группы и 3 останутся.\nmath-habitat-table: 1) Сад. 2) Пруд. 3) 9. 4) 21. 5) 42.\ndata-mini-graph: 1) Животные у пруда. 2) 5. 3) 14.',
    spellingAnswerKey: 'солнце\nвода\nрастение\nпища\nвывод',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 10 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 4 фраз.' },
      { taskId: 'nature-chain-order', answer: 'B → C → D → A; солнце; насекомое; птица.' },
      { taskId: 'nature-needs-sort', answer: 'Растениям: 2,6,9. Животным: 3,7,10. Обоим: 1,4,5,8,11,12.' },
      { taskId: 'math-flower-insect', answer: '24; 8; 19; 4 полные группы и 3 останутся.' },
      { taskId: 'math-habitat-table', answer: 'Сад; пруд; 9; 21; 42.' },
      { taskId: 'data-mini-graph', answer: 'Животные у пруда; 5; 14.' },
      { taskId: 'spelling-connections', answer: 'солнце, вода, растение, пища, вывод.' },
      { taskId: 'creative-nature-chain', answer: 'Цепочка природы и вывод.' },
      { taskId: 'speech-nature-connection', answer: 'Минимум 2 связи по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита поставила цепочку в порядок.',
      'Анита решила задачи с × и ÷.',
      'Анита прочитала таблицу.',
      'Анита сделала мини-график.',
      'Анита нарисовала цепочку природы.',
      'Анита объяснила вывод.'
    ],
    spellingCheck: 'Проверить солнце, вода, растение, пища, вывод.',
    readingCheck: 'Главное — попытка русского чтения и понимание связи.',
    practicalCheck: 'Цепочка должна быть безопасной и учебной, без сбора живых организмов.',
    drawingCriteria: 'Есть солнце, вода, растение, животное/насекомое, стрелки и вывод.',
    commonMistakes: [
      '«Растение растёт само.» Правильно: «Растению нужны условия.»',
      '«Животные не зависят от растений.» Правильно: «Многие животные зависят от растений или других организмов.»',
      '«Связь — это просто линия.» Правильно: «Связь показывает, как одно влияет на другое.»'
    ],
    discussionPrompts: [
      'Что нужно растению?',
      'Почему животному нужна среда?',
      'Что показывает цепочка?',
      'Какой вывод можно сделать?'
    ],
    ifEasy: 'Пусть Анита составит вторую цепочку: солнце → трава → кролик → лиса.',
    ifDifficult: 'Оставить цепочку из 3 элементов: солнце → растение → животное.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратную цепочку или ясный вывод.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не просить ребёнка ловить, трогать, кормить или собирать живые организмы. Использовать картинки, окно, тетрадь и безопасное обсуждение.',
    safetyNotes: 'Не трогать незнакомые растения, грибы, насекомых или животных. Не есть ничего найденного. Не выходить одной для наблюдения.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-chain', label: 'Бонус за аккуратную цепочку природы', amount: 1 },
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'nature-chain-order',
      'nature-needs-sort',
      'math-flower-insect',
      'math-habitat-table',
      'data-mini-graph',
      'spelling-connections',
      'creative-nature-chain',
      'speech-nature-connection'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-8-day-5'
};

export default day4;
